import { useTxPayload } from '@g4g/graphql/src/shop/queries'
import { useShoppingCart } from '@/shop/src/components/shopping-cart/ShoppingCartProvider'
import type { ERC20Names } from '@g4g/wallet-modal/types'
import { useRecipient } from '@/shop/src/components/recipient/RecipientProvider'
import { useSocialAccount } from '@g4g/social-account/components/SocialAccountProvider'
import { BuildTransactionVariables } from '@g4g/graphql/src/shop/__generated__/BuildTransaction'
import { useDebouncedVariable } from '@g4g/utils/src/react/useDebouncedVariable'
import { getCurrency } from '@/shop/src/utils/currency'

/**
 * Creates a payload for a transaction, all from shopping cart contents.
 *
 * @returns The payload which is built by the backend.
 */
export const useShoppingCartTxPayload = ({
  account,
  paymentOption,
}: {
  account: string
  paymentOption: ERC20Names
}) => {
  const cart = useShoppingCart()
  const { currentRecipient } = useRecipient()
  const { socialAccount } = useSocialAccount()

  const [input, debouncing] = useDebouncedVariable<BuildTransactionVariables['input']>(
    {
      purchaserCosId: socialAccount?.userData.userId,
      // Bit of a hack. Query gets skipped if no recipient. Typings too painful to easily fix this.
      recipientCosId: currentRecipient || '',
      paymentCurrency: getCurrency(paymentOption),
      ethAddress: account,
      products: cart.map((product) => ({
        productId: product.productId,
        quantity: product.totalQuantity,
        shopItemId: product.shopItemId,
      })),
    },
    300,
    [socialAccount?.userData.userId, currentRecipient, paymentOption, account, cart]
  )

  const res = useTxPayload({
    input,
  })
  return {
    ...res,
    loading: res.loading || debouncing,
  }
}
